import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Text, Badge, Input, HStack, Flex, IconButton,
} from '@chakra-ui/react';
import { FiX, FiPlus } from 'react-icons/fi';
import api from '../api';

/**
 * ServiceGroupPicker — multi-select with inline create
 *
 * Props:
 *   selected     — array of { id, name } currently selected
 *   onChange      — called with new array of { id, name }
 *   entityType   — 'company' | 'project' | 'invoice' | 'row_group' (for auto-save mode)
 *   entityId     — ID of the entity (for auto-save mode)
 *   autoSave     — if true, saves to junction via API on every add/remove
 */
export default function ServiceGroupPicker({ selected = [], onChange, entityType, entityId, autoSave = false }) {
  const [allGroups, setAllGroups] = useState([]);
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const debounce = useRef(null);

  const fetchGroups = async () => {
    try { const { data } = await api.get('/service-groups'); setAllGroups(data); } catch(e) {}
  };

  useEffect(() => { fetchGroups(); }, []);

  // If autoSave + entityId, load from junction
  useEffect(() => {
    if (autoSave && entityType && entityId) {
      api.get(`/service-groups/for/${entityType}/${entityId}`).then(r => onChange(r.data)).catch(() => {});
    }
  }, [autoSave, entityType, entityId]);

  const saveToJunction = async (newSelected) => {
    if (autoSave && entityType && entityId) {
      try {
        await api.put(`/service-groups/for/${entityType}/${entityId}`, {
          service_group_ids: newSelected.map(s => s.id),
        });
      } catch(e) {}
    }
  };

  const handleAdd = async (group) => {
    if (selected.find(s => s.id === group.id)) return;
    const next = [...selected, group];
    onChange(next);
    await saveToJunction(next);
    setQuery(''); setShowDropdown(false);
  };

  const handleRemove = async (groupId) => {
    const next = selected.filter(s => s.id !== groupId);
    onChange(next);
    await saveToJunction(next);
  };

  const handleCreateNew = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      const { data } = await api.post('/service-groups', { name });
      setAllGroups(prev => [...prev, data]);
      await handleAdd(data);
    } catch(e) {
      // Might already exist, try to find it
      const existing = allGroups.find(g => g.name.toLowerCase() === name.toLowerCase());
      if (existing) await handleAdd(existing);
    }
  };

  const filtered = allGroups.filter(g =>
    !selected.find(s => s.id === g.id) &&
    g.name.toLowerCase().includes(query.toLowerCase())
  );

  const exactMatch = allGroups.find(g => g.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <Box>
      {selected.length > 0 && (
        <Flex gap={1} flexWrap="wrap" mb={1}>
          {selected.map(sg => (
            <Badge key={sg.id} colorScheme="orange" display="flex" alignItems="center" gap={1}>
              {sg.name}
              <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                onClick={() => handleRemove(sg.id)} aria-label="Remove" />
            </Badge>
          ))}
        </Flex>
      )}
      <Box position="relative">
        <HStack spacing={1}>
          <Input size="sm" value={query} placeholder="Search or add service group..."
            onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            onKeyDown={(e) => { if (e.key === 'Enter' && query.trim() && !exactMatch) { e.preventDefault(); handleCreateNew(); } }} />
        </HStack>
        {showDropdown && (query || filtered.length > 0) && (
          <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
            borderRadius="md" boxShadow="lg" zIndex={200} maxH="150px" overflowY="auto">
            {filtered.map(g => (
              <Box key={g.id} px={2} py={1} cursor="pointer" _hover={{ bg:'orange.50' }}
                onMouseDown={(e) => { e.preventDefault(); handleAdd(g); }}>
                {g.name}
              </Box>
            ))}
            {query.trim() && !exactMatch && (
              <Box px={2} py={1} cursor="pointer" _hover={{ bg:'green.50' }} color="green.600" fontWeight="600"
                onMouseDown={(e) => { e.preventDefault(); handleCreateNew(); }}>
                <HStack spacing={1}><FiPlus size={12} /><Text>Create "{query.trim()}"</Text></HStack>
              </Box>
            )}
            {filtered.length === 0 && !query.trim() && (
              <Box px={2} py={1} color="gray.400">No service groups yet</Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
